import { Reducer } from "react";
import { getType } from "typesafe-actions";
import { BankAccount, Card } from "../../models/billing";
import {
  createCardAsync,
  createDepositAsync,
  createDepositClear,
  createRecurringDepositAsync,
  createRecurringDepositClear,
  fetchBankAccountsAsync,
  fetchCardsAsync,
  linkBankAccountAsync,
} from "./actions";
import { BillingAction } from "./types";

type BillingState = {
  bankAccounts: {
    initialized: boolean;
    loading: boolean;
    bankAccounts: BankAccount[];
    linking: boolean;
    linkingError?: string;
  };

  cards: {
    initialized: boolean;
    loading: boolean;
    cards: Card[];
    creating: boolean;
    creatingError?: string;
    recentlyAdded?: Card;
  };

  deposit: {
    loading: boolean;
    error?: string;
    success?: string;
  };

  recurringDeposit: {
    loading: boolean;
    error?: string;
    success?: string;
  };
};

const initialBillingState: BillingState = {
  bankAccounts: {
    initialized: false,
    loading: false,
    linking: false,
    bankAccounts: [],
  },

  cards: {
    initialized: false,
    loading: false,
    creating: false,
    cards: [],
  },

  deposit: {
    loading: false,
  },

  recurringDeposit: {
    loading: false,
  },
};

const billingReducer: Reducer<BillingState, BillingAction> = (
  state: BillingState = initialBillingState,
  action: BillingAction
) => {
  switch (action.type) {
    case getType(fetchBankAccountsAsync.request): {
      return {
        ...state,
        bankAccounts: { ...state.bankAccounts, loading: true },
      };
    }
    case getType(fetchBankAccountsAsync.success): {
      const bankAccounts = action.payload.map((ba) => new BankAccount(ba));
      return {
        ...state,
        bankAccounts: {
          ...state.bankAccounts,
          loading: false,
          initialized: true,
          bankAccounts,
        },
      };
    }
    case getType(fetchBankAccountsAsync.failure): {
      return {
        ...state,
        bankAccounts: {
          ...state.bankAccounts,
          loading: false,
        },
      };
    }

    case getType(linkBankAccountAsync.request): {
      return {
        ...state,
        bankAccounts: {
          ...state.bankAccounts,
          linking: true,
        },
      };
    }
    case getType(linkBankAccountAsync.success): {
      const {
        bankAccounts: { bankAccounts },
      } = state;
      return {
        ...state,
        bankAccounts: {
          ...state.bankAccounts,
          linking: false,
          bankAccounts: [...bankAccounts, new BankAccount(action.payload)],
        },
      };
    }
    case getType(linkBankAccountAsync.failure): {
      return {
        ...state,
        bankAccounts: {
          ...state.bankAccounts,
          linking: false,
          linkingError: action.payload,
        },
      };
    }

    case getType(fetchCardsAsync.request): {
      return {
        ...state,
        cards: { ...state.cards, loading: true },
      };
    }
    case getType(fetchCardsAsync.success): {
      const cards = action.payload;
      return {
        ...state,
        cards: {
          ...state.cards,
          loading: true,
          initialized: true,
          cards,
          recentlyAdded: undefined,
        },
      };
    }
    case getType(fetchCardsAsync.failure): {
      return {
        ...state,
        cards: {
          ...state.cards,
          loading: true,
        },
      };
    }

    case getType(createCardAsync.request): {
      return {
        ...state,
        cards: {
          ...state.cards,
          creating: true,
        },
      };
    }
    case getType(createCardAsync.success): {
      const {
        cards: { cards },
      } = state;
      return {
        ...state,
        cards: {
          ...state.cards,
          creating: false,
          cards: [...cards, action.payload],
          recentlyAdded: action.payload,
        },
      };
    }
    case getType(createCardAsync.failure): {
      return {
        ...state,
        cards: {
          ...state.cards,
          creating: false,
          creatingError: action.payload,
        },
      };
    }

    case getType(createDepositAsync.request): {
      return {
        ...state,
        deposit: {
          loading: true,
        },
      };
    }
    case getType(createDepositAsync.success): {
      return {
        ...state,
        deposit: {
          loading: false,
          success: action.payload,
        },
      };
    }
    case getType(createDepositAsync.failure): {
      return {
        ...state,
        deposit: {
          loading: false,
          error: action.payload,
        },
      };
    }
    case getType(createDepositClear): {
      return {
        ...state,
        deposit: {
          loading: false,
        },
      };
    }

    case getType(createRecurringDepositAsync.request): {
      return {
        ...state,
        recurringDeposit: {
          loading: true,
        },
      };
    }
    case getType(createRecurringDepositAsync.success): {
      return {
        ...state,
        recurringDeposit: {
          loading: false,
          success: action.payload,
        },
      };
    }
    case getType(createRecurringDepositAsync.failure): {
      return {
        ...state,
        recurringDeposit: {
          loading: false,
          error: action.payload,
        },
      };
    }
    case getType(createRecurringDepositClear): {
      return {
        ...state,
        recurringDeposit: {
          loading: false,
        },
      };
    }

    default:
      return state;
  }
};

export default billingReducer;
